import { useState } from "react";
import {
  Plus,
  FileText,
  Search,
  MoreHorizontal,
  Eye,
  Copy,
  Trash2,
  ChevronLeft,
  ChevronRight,
  LayoutGrid,
  List,
  Ban,
  Folder,
  Tag,
  X,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Badge } from "@/components/ui/badge";
import {
  ActionSheet,
  ActionSheetContent,
  ActionSheetItem,
  ActionSheetSeparator,
} from "@/components/ui/bottom-sheet";
import { Button, FloatingActionButton } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import {
  SegmentedControl,
  SegmentedControlItem,
} from "@/components/ui/mobile-nav";
import { PdfThumbnail } from "@/components/ui/pdf-thumbnail";
import { HorizontalScroll } from "@/components/ui/responsive-table";
import { Skeleton } from "@/components/ui/skeleton";
import { EnvelopeStatusBadge } from "@/components/ui/StatusBadge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useIsMobile } from "@/hooks/useMobileUtils";
import {
  useEnvelopes,
  useDeleteEnvelope,
  useVoidEnvelope,
  useDuplicateEnvelope,
  useFolders,
  useTags,
} from "@/hooks/useQueries";
import { useAuthStore } from "@/store/auth.store";
import { formatDateShort } from "@/utils/dateFormat";
import { canUseFeature } from "@/utils/permissions";

// Display sender: the per-envelope override when one was supplied at
// creation, otherwise the logged-in user (the list only contains the
// requester's own envelopes, so the owner is always the current user)
const getSenderDisplay = (envelope, user) => {
  if (envelope.sender?.email || envelope.sender?.name) {
    return {
      name: envelope.sender.name || envelope.sender.email,
      email: envelope.sender.email,
    };
  }
  const name = `${user?.firstName || ""} ${user?.lastName || ""}`.trim();
  return { name: name || user?.email, email: user?.email };
};

// Envelope card for grid view
const EnvelopeCard = ({
  envelope,
  onView,
  onDuplicate,
  onVoid,
  onDelete,
  canVoid,
}) => {
  const { t } = useTranslation();
  const { user } = useAuthStore();
  const isMobile = useIsMobile();
  const [actionSheetOpen, setActionSheetOpen] = useState(false);
  const canVoidEnvelope =
    canVoid && ["sent", "viewed", "partially_signed"].includes(envelope.status);

  // Build thumbnail URL - try multiple sources for document ID
  const documentId = envelope.firstDocumentId || envelope.documents?.[0]?.id;
  const thumbnailUrl = documentId
    ? `${import.meta.env.VITE_API_URL || "/api/v1"}/envelopes/${envelope.id}/documents/${documentId}/download?type=original`
    : null;

  const handleMoreClick = (e) => {
    e.stopPropagation();
    if (isMobile) {
      setActionSheetOpen(true);
    }
  };

  return (
    <>
      <Card
        className="group cursor-pointer card-pressable hover:shadow-md transition-shadow overflow-hidden"
        onClick={() => onView(envelope.id)}
      >
        <div className="h-40 bg-muted flex items-center justify-center relative">
          {thumbnailUrl ? (
            <PdfThumbnail
              url={thumbnailUrl}
              cacheKey={`envelope-${envelope.id}`}
              className="w-full h-full"
              width={250}
              fallback={
                <div className="text-center">
                  <FileText
                    className="h-8 w-8 text-muted-foreground mx-auto mb-2"
                    aria-hidden="true"
                  />
                  <EnvelopeStatusBadge status={envelope?.status} />
                </div>
              }
              overlay={
                <div className="absolute bottom-2 left-2">
                  <EnvelopeStatusBadge status={envelope?.status} />
                </div>
              }
            />
          ) : (
            <div className="text-center">
              <FileText
                className="h-8 w-8 text-muted-foreground mx-auto mb-2"
                aria-hidden="true"
              />
              <EnvelopeStatusBadge status={envelope?.status} />
            </div>
          )}

          <div className="absolute top-2 right-2 z-10">
            {isMobile ? (
              <Button
                variant="secondary"
                size="icon"
                className="h-10 w-10 touch-target"
                onClick={handleMoreClick}
              >
                <MoreHorizontal className="h-5 w-5" />
              </Button>
            ) : (
              <DropdownMenu>
                <DropdownMenuTrigger
                  asChild
                  onClick={(e) => e.stopPropagation()}
                >
                  <Button variant="secondary" size="icon" className="h-8 w-8">
                    <MoreHorizontal className="h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem
                    onClick={(e) => {
                      e.stopPropagation();
                      onView(envelope.id);
                    }}
                  >
                    <Eye className="mr-2 h-4 w-4" />
                    {t("common.view")}
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={(e) => {
                      e.stopPropagation();
                      onDuplicate(envelope.id);
                    }}
                  >
                    <Copy className="mr-2 h-4 w-4" />
                    {t("common.copy")}
                  </DropdownMenuItem>
                  {canVoidEnvelope && (
                    <>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem
                        onClick={(e) => {
                          e.stopPropagation();
                          onVoid(envelope);
                        }}
                        className="text-amber-600 focus:text-amber-600"
                      >
                        <Ban className="mr-2 h-4 w-4" />
                        {t("envelopes.void")}
                      </DropdownMenuItem>
                    </>
                  )}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    onClick={(e) => {
                      e.stopPropagation();
                      onDelete(envelope);
                    }}
                    className="text-destructive focus:text-destructive"
                  >
                    <Trash2 className="mr-2 h-4 w-4" />
                    {t("common.delete")}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </div>
        </div>

        <CardContent className="p-4 dark:bg-background">
          <h3 className="font-medium truncate mb-1">
            {envelope.title || t("common.untitled")}
          </h3>
          <p className="text-sm text-muted-foreground truncate">
            {getSenderDisplay(envelope, user).name}
          </p>
          <p className="text-sm text-muted-foreground">
            {formatDateShort(envelope.updatedAt, user)}
          </p>
        </CardContent>
      </Card>

      {/* Mobile Action Sheet */}
      {isMobile && (
        <ActionSheet open={actionSheetOpen} onOpenChange={setActionSheetOpen}>
          <ActionSheetContent title={envelope.title || t("common.untitled")}>
            <ActionSheetItem
              onClick={() => {
                setActionSheetOpen(false);
                onView(envelope.id);
              }}
            >
              <Eye className="h-5 w-5" />
              {t("common.view")}
            </ActionSheetItem>
            <ActionSheetItem
              onClick={() => {
                setActionSheetOpen(false);
                onDuplicate(envelope.id);
              }}
            >
              <Copy className="h-5 w-5" />
              {t("common.copy")}
            </ActionSheetItem>
            {canVoidEnvelope && (
              <>
                <ActionSheetSeparator />
                <ActionSheetItem
                  onClick={() => {
                    setActionSheetOpen(false);
                    onVoid(envelope);
                  }}
                >
                  <Ban className="h-5 w-5 text-amber-600" />
                  <span className="text-amber-600">{t("envelopes.void")}</span>
                </ActionSheetItem>
              </>
            )}
            <ActionSheetSeparator />
            <ActionSheetItem
              destructive
              onClick={() => {
                setActionSheetOpen(false);
                onDelete(envelope);
              }}
            >
              <Trash2 className="h-5 w-5" />
              {t("common.delete")}
            </ActionSheetItem>
          </ActionSheetContent>
        </ActionSheet>
      )}
    </>
  );
};

// Table row for list view (desktop)
const EnvelopeRow = ({
  envelope,
  onView,
  onDuplicate,
  onVoid,
  onDelete,
  canVoid,
}) => {
  const { t } = useTranslation();
  const { user } = useAuthStore();
  const canVoidEnvelope =
    canVoid && ["sent", "viewed", "partially_signed"].includes(envelope.status);
  const sender = getSenderDisplay(envelope, user);

  return (
    <TableRow className="cursor-pointer" onClick={() => onView(envelope.id)}>
      <TableCell>
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary/10">
            <FileText className="h-5 w-5 text-primary" />
          </div>
          <div>
            <p className="font-medium">
              {envelope.title || t("common.untitled")}
            </p>
            <p className="text-sm text-muted-foreground">
              {envelope.recipientCount} recipient
              {envelope.recipientCount !== 1 ? "s" : ""}
            </p>
          </div>
        </div>
      </TableCell>
      <TableCell>
        <EnvelopeStatusBadge status={envelope?.status} />
      </TableCell>
      <TableCell>
        <p className="truncate max-w-48">{sender.name}</p>
        {sender.email && sender.email !== sender.name && (
          <p className="text-xs text-muted-foreground truncate max-w-48">
            {sender.email}
          </p>
        )}
      </TableCell>
      <TableCell className="text-muted-foreground">
        {formatDateShort(envelope.updatedAt, user)}
      </TableCell>
      <TableCell onClick={(e) => e.stopPropagation()}>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon" className="h-8 w-8">
              <MoreHorizontal className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={() => onView(envelope.id)}>
              <Eye className="mr-2 h-4 w-4" />
              {t("common.view")}
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => onDuplicate(envelope.id)}>
              <Copy className="mr-2 h-4 w-4" />
              {t("common.copy")}
            </DropdownMenuItem>
            {canVoidEnvelope && (
              <>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onClick={() => onVoid(envelope)}
                  className="text-amber-600 focus:text-amber-600"
                >
                  <Ban className="mr-2 h-4 w-4" />
                  {t("envelopes.void")}
                </DropdownMenuItem>
              </>
            )}
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onClick={() => onDelete(envelope)}
              className="text-destructive focus:text-destructive"
            >
              <Trash2 className="mr-2 h-4 w-4" />
              {t("common.delete")}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </TableCell>
    </TableRow>
  );
};

// Loading skeleton
const EnvelopesSkeleton = () => {
  const isMobile = useIsMobile();
  return (
    <div className="space-y-6">
      <div className="flex justify-between">
        <div className="space-y-2">
          <Skeleton className="h-8 w-48" />
          <Skeleton className="h-4 w-64" />
        </div>
        {!isMobile && <Skeleton className="h-10 w-40" />}
      </div>
      {isMobile && <Skeleton className="h-10 w-full" />}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
        {[1, 2, 3, 4, 5, 6, 7, 8].map((i) => (
          <div key={i} className="rounded-lg border bg-card overflow-hidden">
            <Skeleton className="h-40 rounded-none" />
            <div className="p-4 space-y-2">
              <Skeleton className="h-5 w-3/4" />
              <Skeleton className="h-4 w-1/2" />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// Empty state
const EmptyState = ({ hasFilters, onClear }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  return (
    <Card>
      <CardContent className="flex flex-col items-center justify-center py-12 text-center">
        <div className="flex h-16 w-16 items-center justify-center rounded-full bg-muted mb-4">
          <FileText className="h-8 w-8 text-muted-foreground" />
        </div>
        <h3 className="text-lg font-semibold mb-1">
          {t("envelopes.noEnvelopes")}
        </h3>
        <p className="text-sm text-muted-foreground mb-4 max-w-sm">
          {hasFilters
            ? t("envelopes.noEnvelopesFiltered")
            : t("envelopes.noEnvelopesYet")}
        </p>
        {hasFilters ? (
          <Button
            variant="outline"
            onClick={onClear}
            size="lg"
            className="touch-target"
          >
            {t("common.tryAdjustingFilters")}
          </Button>
        ) : (
          <Button
            onClick={() => navigate("/envelopes/new")}
            size="lg"
            className="touch-target"
          >
            <Plus className="mr-2 h-4 w-4" />
            {t("envelopes.newEnvelope")}
          </Button>
        )}
      </CardContent>
    </Card>
  );
};

// Pagination component
const Pagination = ({ currentPage, totalPages, onPageChange, isMobile }) => {
  const { t } = useTranslation();
  if (totalPages <= 1) return null;

  if (isMobile) {
    return (
      <div className="flex items-center justify-between py-4">
        <Button
          variant="outline"
          size="mobile"
          disabled={currentPage <= 1}
          onClick={() => onPageChange(currentPage - 1)}
          className="flex-1 mr-2"
        >
          <ChevronLeft className="h-4 w-4 mr-1" />
          {t("common.previous")}
        </Button>
        <span className="text-sm text-muted-foreground px-4">
          {currentPage} / {totalPages}
        </span>
        <Button
          variant="outline"
          size="mobile"
          disabled={currentPage >= totalPages}
          onClick={() => onPageChange(currentPage + 1)}
          className="flex-1 ml-2"
        >
          {t("common.next")}
          <ChevronRight className="h-4 w-4 ml-1" />
        </Button>
      </div>
    );
  }

  const getPageNumbers = () => {
    const pages = [];
    const showPages = 5;
    let startPage = Math.max(1, currentPage - Math.floor(showPages / 2));
    const endPage = Math.min(totalPages, startPage + showPages - 1);
    if (endPage - startPage + 1 < showPages) {
      startPage = Math.max(1, endPage - showPages + 1);
    }
    for (let i = startPage; i <= endPage; i++) pages.push(i);
    return pages;
  };

  return (
    <div className="flex items-center justify-between">
      <p className="text-sm text-muted-foreground">
        {t("common.page")} {currentPage} {t("common.of")} {totalPages}
      </p>
      <div className="flex items-center gap-1">
        <Button
          variant="outline"
          size="sm"
          disabled={currentPage <= 1}
          onClick={() => onPageChange(currentPage - 1)}
        >
          <ChevronLeft className="h-4 w-4 mr-1" />
          {t("common.previous")}
        </Button>
        <div className="flex gap-1">
          {getPageNumbers().map((pageNum) => (
            <Button
              key={pageNum}
              variant={currentPage === pageNum ? "default" : "outline"}
              size="sm"
              className="w-8 h-8 p-0"
              onClick={() => onPageChange(pageNum)}
            >
              {pageNum}
            </Button>
          ))}
        </div>
        <Button
          variant="outline"
          size="sm"
          disabled={currentPage >= totalPages}
          onClick={() => onPageChange(currentPage + 1)}
        >
          {t("common.next")}
          <ChevronRight className="h-4 w-4 ml-1" />
        </Button>
      </div>
    </div>
  );
};

// Main component
export const Envelopes = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const [searchParams, setSearchParams] = useSearchParams();

  const [searchInput, setSearchInput] = useState(
    searchParams.get("search") || "",
  );
  const [viewMode, setViewMode] = useState("grid");
  const [voidDialog, setVoidDialog] = useState({ open: false, envelope: null });
  const [deleteDialog, setDeleteDialog] = useState({
    open: false,
    envelope: null,
  });

  const { user } = useAuthStore();
  const canVoid = canUseFeature(user, "voidEnvelope");

  const currentStatus = searchParams.get("status") || "all";
  const currentPage = parseInt(searchParams.get("page") || "1", 10);
  const currentSearch = searchParams.get("search") || "";
  const currentFolderId = searchParams.get("folder") || undefined;
  const currentTagId = searchParams.get("tag") || undefined;

  const { data, isLoading } = useEnvelopes({
    page: currentPage,
    limit: 12,
    status: currentStatus === "all" ? undefined : currentStatus,
    search: currentSearch || undefined,
    sortBy: "updated_at",
    sortOrder: "desc",
    folderId: currentFolderId,
    tagId: currentTagId,
  });

  const envelopes = data?.data || [];
  const pagination = data?.pagination || { page: 1, limit: 12, total: 0 };
  const totalPages = Math.ceil(pagination.total / pagination.limit);

  const { data: folders = [] } = useFolders({ enabled: !!currentFolderId });
  const { data: tags = [] } = useTags({ enabled: !!currentTagId });
  const currentFolder = currentFolderId
    ? folders.find((f) => f.id === currentFolderId)
    : null;
  const currentTag = currentTagId
    ? tags.find((t) => t.id === currentTagId)
    : null;

  const deleteEnvelope = useDeleteEnvelope();
  const voidEnvelope = useVoidEnvelope();
  const duplicateEnvelope = useDuplicateEnvelope();

  const handleStatusChange = (status) => {
    const newParams = new URLSearchParams(searchParams);
    if (status === "all") newParams.delete("status");
    else newParams.set("status", status);
    newParams.delete("page");
    setSearchParams(newParams);
  };

  const handleSearch = (e) => {
    e.preventDefault();
    const newParams = new URLSearchParams(searchParams);
    if (searchInput) newParams.set("search", searchInput);
    else newParams.delete("search");
    newParams.delete("page");
    setSearchParams(newParams);
  };

  const handlePageChange = (page) => {
    const newParams = new URLSearchParams(searchParams);
    newParams.set("page", page.toString());
    setSearchParams(newParams);
  };

  const handleClearFilters = () => {
    setSearchInput("");
    setSearchParams({});
  };
  const handleClearFolderFilter = () => {
    const p = new URLSearchParams(searchParams);
    p.delete("folder");
    p.delete("page");
    setSearchParams(p);
  };
  const handleClearTagFilter = () => {
    const p = new URLSearchParams(searchParams);
    p.delete("tag");
    p.delete("page");
    setSearchParams(p);
  };

  const handleView = (id) => navigate(`/envelopes/${id}`);
  const handleDuplicate = (id) => duplicateEnvelope.mutate(id);
  const handleVoid = (envelope) => setVoidDialog({ open: true, envelope });
  const confirmVoid = () => {
    if (!voidDialog.envelope) return;
    voidEnvelope.mutate(
      { id: voidDialog.envelope.id, reason: "Voided by user" },
      {
        onSuccess: () => setVoidDialog({ open: false, envelope: null }),
      },
    );
  };
  const handleDelete = (envelope) => setDeleteDialog({ open: true, envelope });
  const confirmDelete = () => {
    if (!deleteDialog.envelope) return;
    deleteEnvelope.mutate(deleteDialog.envelope.id, {
      onSuccess: () => setDeleteDialog({ open: false, envelope: null }),
    });
  };

  const hasFilters =
    currentStatus !== "all" || currentSearch || currentFolderId || currentTagId;

  const renderContent = () => {
    if (envelopes.length === 0) {
      return (
        <EmptyState hasFilters={hasFilters} onClear={handleClearFilters} />
      );
    }

    if (isMobile || viewMode === "grid") {
      return (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {envelopes.map((envelope) => (
            <EnvelopeCard
              key={envelope.id}
              envelope={envelope}
              onView={handleView}
              onDuplicate={handleDuplicate}
              onVoid={handleVoid}
              onDelete={handleDelete}
              canVoid={canVoid}
            />
          ))}
        </div>
      );
    }

    return (
      <Card>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t("common.name")}</TableHead>
              <TableHead>{t("common.status")}</TableHead>
              <TableHead>{t("common.sender")}</TableHead>
              <TableHead>{t("common.updated")}</TableHead>
              <TableHead className="w-12.5" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {envelopes.map((envelope) => (
              <EnvelopeRow
                key={envelope.id}
                envelope={envelope}
                onView={handleView}
                onDuplicate={handleDuplicate}
                onVoid={handleVoid}
                onDelete={handleDelete}
                canVoid={canVoid}
              />
            ))}
          </TableBody>
        </Table>
      </Card>
    );
  };

  if (isLoading && envelopes.length === 0) return <EnvelopesSkeleton />;

  return (
    <div className="space-y-6 pb-20 sm:pb-0">
      {/* Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold fon-heading tracking-tight">
            {t("envelopes.title")}
          </h1>
          <p className="text-muted-foreground">{t("envelopes.subtitle")}</p>
        </div>
        {!isMobile && (
          <Button onClick={() => navigate("/envelopes/new")}>
            <Plus className="mr-2 h-4 w-4" />
            {t("envelopes.newEnvelope")}
          </Button>
        )}
      </div>

      {/* Filters */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center">
          {isMobile ? (
            <HorizontalScroll className="pb-0!">
              <SegmentedControl
                value={currentStatus}
                onValueChange={handleStatusChange}
                className="shrink-0"
              >
                <SegmentedControlItem value="all">
                  {t("common.all")}
                </SegmentedControlItem>
                <SegmentedControlItem value="draft">
                  {t("envelopes.status.draft")}
                </SegmentedControlItem>
                <SegmentedControlItem value="sent">
                  {t("envelopes.status.sent")}
                </SegmentedControlItem>
                <SegmentedControlItem value="completed">
                  {t("envelopes.status.completed")}
                </SegmentedControlItem>
              </SegmentedControl>
            </HorizontalScroll>
          ) : (
            <Tabs value={currentStatus} onValueChange={handleStatusChange}>
              <TabsList>
                <TabsTrigger value="all">{t("common.all")}</TabsTrigger>
                <TabsTrigger value="draft">
                  {t("envelopes.status.draft")}
                </TabsTrigger>
                <TabsTrigger value="sent">
                  {t("envelopes.status.sent")}
                </TabsTrigger>
                <TabsTrigger value="completed">
                  {t("envelopes.status.completed")}
                </TabsTrigger>
              </TabsList>
            </Tabs>
          )}

          <form onSubmit={handleSearch} className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder={t("envelopes.search")}
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              className={isMobile ? "pl-9 w-full h-11" : "pl-9 w-64"}
            />
          </form>
        </div>

        {!isMobile && (
          <div className="flex items-center border rounded-lg p-1">
            <Button
              variant={viewMode === "grid" ? "secondary" : "ghost"}
              size="sm"
              className="h-8 w-8 p-0"
              onClick={() => setViewMode("grid")}
            >
              <LayoutGrid className="h-4 w-4" />
            </Button>
            <Button
              variant={viewMode === "list" ? "secondary" : "ghost"}
              size="sm"
              className="h-8 w-8 p-0"
              onClick={() => setViewMode("list")}
            >
              <List className="h-4 w-4" />
            </Button>
          </div>
        )}
      </div>

      {/* Active filters */}
      {(currentFolderId || currentTagId) && (
        <div className="flex flex-wrap gap-2">
          {currentFolderId && (
            <Badge
              variant="secondary"
              className="flex items-center gap-1.5 px-3 py-1.5"
            >
              <Folder
                className="h-3.5 w-3.5"
                style={{ color: currentFolder?.color }}
              />
              <span>
                {currentFolder?.name || t("envelopes.filterByFolder")}
              </span>
              <button
                onClick={handleClearFolderFilter}
                className="ml-1 hover:bg-muted rounded-full p-0.5 touch-target"
              >
                <X className="h-3 w-3" />
              </button>
            </Badge>
          )}
          {currentTagId && (
            <Badge
              variant="secondary"
              className="flex items-center gap-1.5 px-3 py-1.5"
            >
              <Tag
                className="h-3.5 w-3.5"
                style={{ color: currentTag?.color }}
              />
              <span>{currentTag?.name || t("envelopes.filterByTag")}</span>
              <button
                onClick={handleClearTagFilter}
                className="ml-1 hover:bg-muted rounded-full p-0.5 touch-target"
              >
                <X className="h-3 w-3" />
              </button>
            </Badge>
          )}
        </div>
      )}

      {/* Content */}
      {renderContent()}

      {/* Pagination */}
      {totalPages > 1 && (
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={handlePageChange}
          isMobile={isMobile}
        />
      )}

      {/* Mobile FAB */}
      {isMobile && (
        <FloatingActionButton onClick={() => navigate("/envelopes/new")}>
          <Plus className="h-6 w-6" />
        </FloatingActionButton>
      )}

      {/* Dialogs - auto bottom sheet on mobile */}
      <Dialog
        open={voidDialog.open}
        onOpenChange={(open) =>
          !open && setVoidDialog({ open: false, envelope: null })
        }
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t("envelopes.voidEnvelope")}</DialogTitle>
            <DialogDescription>
              {t("envelopes.voidEnvelopeDesc")}
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <p className="font-medium">
              {voidDialog.envelope?.title || t("common.untitled")}
            </p>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setVoidDialog({ open: false, envelope: null })}
              size={isMobile ? "mobile" : "default"}
            >
              {t("common.cancel")}
            </Button>
            <Button
              variant="destructive"
              onClick={confirmVoid}
              disabled={voidEnvelope.isPending}
              size={isMobile ? "mobile" : "default"}
            >
              {voidEnvelope.isPending
                ? t("common.loading")
                : t("envelopes.void")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={deleteDialog.open}
        onOpenChange={(open) =>
          !open && setDeleteDialog({ open: false, envelope: null })
        }
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t("envelopes.deleteEnvelope")}</DialogTitle>
            <DialogDescription>
              {t("envelopes.deleteEnvelopeDesc")}
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <p className="font-medium">
              {deleteDialog.envelope?.title || t("common.untitled")}
            </p>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setDeleteDialog({ open: false, envelope: null })}
              size={isMobile ? "mobile" : "default"}
            >
              {t("common.cancel")}
            </Button>
            <Button
              variant="destructive"
              onClick={confirmDelete}
              disabled={deleteEnvelope.isPending}
              size={isMobile ? "mobile" : "default"}
            >
              {deleteEnvelope.isPending
                ? t("common.deleting")
                : t("common.delete")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Envelopes;
